import { useEffect, useState } from 'react';
import { Check, Circle, Sparkles } from 'lucide-react';
import { useStore } from '../store.js';

/**
 * The readiness ladder — a 4-rung progression that a Remedy climbs as
 * the author improves it:
 *
 *   Draft      → any parsed doc
 *   Linted     → 0 errors, 0 warnings, at least one feature
 *   Compiled   → embedded (in-memory build succeeded in this session)
 *   Published  → marketplace presence (Wave 2 — off in v0)
 *
 * Visible above the main workspace area. The current rung is lit in
 * copper-green; completed rungs are muted green; future rungs are grey.
 * Climbing to a new rung triggers a short pulse + progress animation.
 */

type Rung = 'draft' | 'linted' | 'compiled' | 'published';

const RUNGS: { id: Rung; label: string; hint: string }[] = [
  { id: 'draft', label: 'Draft', hint: 'Parsed and well-formed' },
  { id: 'linted', label: 'Linted', hint: '0 errors · 0 warnings' },
  { id: 'compiled', label: 'Compiled', hint: 'Embeddings built' },
  { id: 'published', label: 'Published', hint: 'On the marketplace (Wave 2)' },
];

function computeRung(s: ReturnType<typeof useStore.getState>): Rung {
  if (!s.doc) return 'draft';
  if (s.build.status === 'done' && s.build.artifact) return 'compiled';
  if (s.errors.length === 0 && s.warnings.length === 0 && (s.doc.features?.length ?? 0) > 0) {
    return 'linted';
  }
  return 'draft';
}

export function ReadinessLadder() {
  const state = useStore();
  const current = computeRung(state);
  const currentIdx = RUNGS.findIndex((r) => r.id === current);

  const [pulsing, setPulsing] = useState<Rung | null>(null);
  const [prev, setPrev] = useState<Rung>(current);
  useEffect(() => {
    if (current !== prev) {
      const prevIdx = RUNGS.findIndex((r) => r.id === prev);
      // Only pulse when climbing up (not when a warning drops you back).
      if (currentIdx > prevIdx) {
        setPulsing(current);
        const id = setTimeout(() => setPulsing(null), 1400);
        return () => clearTimeout(id);
      }
      setPrev(current);
    }
  }, [current, prev, currentIdx]);
  useEffect(() => setPrev(current), [current]);

  if (!state.doc) return null;

  // Progress through the current rung — for linted it's inverse of warnings,
  // for compiled it's the embed progress, otherwise show 100% filled on
  // reached rungs and 0% on future ones.
  const progressFor = (rung: Rung, idx: number): number => {
    if (idx < currentIdx) return 100;
    if (idx > currentIdx) return 0;
    if (rung === 'compiled' && state.build.status === 'running' && state.build.progress) {
      return Math.round((state.build.progress.done / Math.max(1, state.build.progress.total)) * 100);
    }
    return 100;
  };

  return (
    <div className="border-b border-ink-800 bg-ink-900/40 px-4 py-2.5">
      <div className="flex items-center gap-3">
        <span className="label shrink-0">Readiness</span>
        <ol className="flex flex-1 items-stretch gap-0">
          {RUNGS.map((r, i) => {
            const reached = i <= currentIdx;
            const isCurrent = i === currentIdx;
            const pct = progressFor(r.id, i);
            const pulseClass = pulsing === r.id ? 'tick-pulse' : '';
            return (
              <li
                key={r.id}
                className={`group relative flex-1 ${pulseClass}`}
                title={r.hint}
              >
                <div className="flex items-center gap-2 px-2.5 py-1.5">
                  <span
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full border shrink-0 transition-all ${
                      reached
                        ? isCurrent
                          ? 'border-accent-500 bg-accent-500/15 text-accent-500'
                          : 'border-status-deployed bg-status-deployed/10 text-status-deployed'
                        : 'border-ink-700 text-ink-600'
                    }`}
                  >
                    {reached ? (
                      r.id === 'published' ? <Sparkles size={11} strokeWidth={2} /> : <Check size={11} strokeWidth={3} />
                    ) : (
                      <Circle size={5} strokeWidth={0} fill="currentColor" />
                    )}
                  </span>
                  <div className="min-w-0 leading-tight">
                    <div className={`mono text-[11px] font-medium tracking-wide ${
                      isCurrent
                        ? 'text-accent-500'
                        : reached
                          ? 'text-status-deployed'
                          : 'text-ink-500'
                    }`}>
                      {r.label}
                    </div>
                    <div className="text-[10px] text-ink-500 hidden md:block">{r.hint}</div>
                  </div>
                </div>
                {/* Rung progress bar */}
                <div className="absolute inset-x-2.5 bottom-0 h-[2px] bg-ink-800 rounded overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      isCurrent ? 'bg-accent-500' : reached ? 'bg-status-deployed' : 'bg-ink-700'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
